import React from 'react';
import { mergeStyleSets } from '@fluentui/react';
import { X } from '@phosphor-icons/react';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { FeatureProps } from './types';
import { HIDDEN_DETAIL_KEYS } from './constants';

const p = boroondaraPalette;

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(val: any): string {
  if (val === null || val === undefined) return '\u2014';
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return val.toLocaleString();
  return String(val);
}

interface FeatureDetailProps {
  feature: FeatureProps;
  onClose: () => void;
}

const styles = mergeStyleSets({
  panel: {
    padding: 24,
    borderRadius: 4,
    background: '#FFFFFF',
    border: `1px solid ${p.border}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: 24,
    '@media (max-width: 768px)': { padding: 16 },
  } as any,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  name: { fontSize: 18, fontWeight: 700, color: p.text },
  badge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    color: p.text3,
    background: p.bg2,
    border: `1px solid ${p.borderSoft}`,
    borderRadius: 999,
    padding: '2px 10px',
    marginTop: 4,
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    selectors: { ':hover': { background: p.bg2 } },
  } as any,
  desc: { fontSize: 13, color: p.text2, lineHeight: '20px', marginBottom: 16 },
  propsGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 0 },
  propRow: {
    display: 'flex',
    padding: '8px 0',
    borderBottom: `1px solid ${p.borderSoft}`,
    fontSize: 13,
    '@media (max-width: 768px)': { flexDirection: 'column', gap: 2 },
  } as any,
  propKey: { width: 180, flexShrink: 0, fontWeight: 600, color: p.text2 },
  propValue: { flex: 1, color: p.text },
});

export default function FeatureDetail({ feature, onClose }: FeatureDetailProps) {
  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <div className={styles.name}>{feature.name}</div>
          {feature.layer_name && <div className={styles.badge}>{feature.layer_name}</div>}
        </div>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close detail panel">
          <X size={12} weight="bold" style={{ color: p.text3 }} />
        </button>
      </div>
      {feature.description && <div className={styles.desc}>{feature.description}</div>}
      <div className={styles.propsGrid}>
        {Object.entries(feature)
          .filter(([key, val]) => !HIDDEN_DETAIL_KEYS.has(key) && val !== null && val !== undefined)
          .map(([key, val]) => (
            <div key={key} className={styles.propRow}>
              <span className={styles.propKey}>{formatKey(key)}</span>
              <span className={styles.propValue}>{formatValue(val)}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
